import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Configuration for `useZoomPan`. All bounds are inclusive.
 */
export interface UseZoomPanOptions {
  /** Minimum zoom level (default 1, no zoom-out below original). */
  minZoom?: number;
  /** Maximum zoom level (default 15). */
  maxZoom?: number;
  /** Button-zoom multiplier (default 1.3). */
  zoomStep?: number;
  /** Wheel-zoom multiplier per tick (default 1.15). */
  wheelStep?: number;
  /** Target zoom level used by `zoomToBox` when the bbox size is unknown. */
  autoZoomLevel?: number;
  /** Animation duration in ms for button / auto-zoom / double-click. */
  animationMs?: number;
  /** Animation duration in ms for wheel zoom (snappier). */
  wheelAnimationMs?: number;
}

/**
 * Normalized 0–1 bounding box used by `zoomToBox`.
 */
export interface NormalizedBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Headless zoom/pan controller for the shared `ImageViewer` primitive.
 *
 * Encapsulates:
 *  - zoom range + clamping
 *  - pan clamping (image edges cannot scroll past viewport center)
 *  - smooth transitions during programmatic changes
 *  - mouse wheel zoom (non-passive listener via `containerRef`)
 *  - double-click-to-zoom-at-point (and double-click-to-reset when near max)
 *  - mouse drag + single-touch drag panning
 *  - `zoomToBox(bbox)` — animate to a normalized 0–1 bbox so it fills ~30% viewport
 *
 * Math model: the consumer renders the image inside
 *   `transform: scale(zoom) translate(panOffset.x%, panOffset.y%)` with
 *   `transformOrigin: center`. See `clampPan` derivation in source comments.
 *
 * Returns ready-to-spread DOM handlers and the current `{ zoom, panOffset }`
 * for the consumer to apply to the inner Box. `containerRef` must be attached
 * to the outer viewport element.
 */
export function useZoomPan(options: UseZoomPanOptions = {}) {
  const {
    minZoom = 1,
    maxZoom = 15,
    zoomStep = 1.3,
    wheelStep = 1.15,
    autoZoomLevel = 3,
    animationMs = 300,
    wheelAnimationMs = 150,
  } = options;

  const [zoom, setZoom] = useState(minZoom);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [animating, setAnimating] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const dragStartPanRef = useRef({ x: 0, y: 0 });
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerAnimation = useCallback((durationMs: number) => {
    setAnimating(true);
    if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
    animationTimerRef.current = setTimeout(() => setAnimating(false), durationMs);
  }, []);

  useEffect(() => () => {
    if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
  }, []);

  /**
   * Clamp pan so the image edge cannot scroll past the viewport center.
   *
   * Derivation: with `scale(z) translate(x%, y%)` and `transformOrigin: center`,
   * the visible region in element coords is
   *   [0.5 - 0.5/z - x/100, 0.5 + 0.5/z - x/100]
   * Forcing both endpoints to remain inside `[0, 1]` gives
   *   x/100 ∈ [-(0.5 - 0.5/z), +(0.5 - 0.5/z)]
   */
  const clampPan = useCallback((px: number, py: number, z: number) => {
    if (z <= 1) return { x: 0, y: 0 };
    const maxPan = (0.5 - 0.5 / z) * 100;
    return {
      x: Math.max(-maxPan, Math.min(maxPan, px)),
      y: Math.max(-maxPan, Math.min(maxPan, py)),
    };
  }, []);

  // --- Zoom helpers ---
  const zoomIn = useCallback(() => {
    triggerAnimation(250);
    setZoom(prev => Math.min(prev * zoomStep, maxZoom));
  }, [maxZoom, zoomStep, triggerAnimation]);

  const zoomOut = useCallback(() => {
    triggerAnimation(250);
    setZoom(prev => {
      const next = Math.max(prev / zoomStep, minZoom);
      if (next <= minZoom) setPanOffset({ x: 0, y: 0 });
      return next;
    });
  }, [minZoom, zoomStep, triggerAnimation]);

  const reset = useCallback(() => {
    triggerAnimation(animationMs);
    setZoom(minZoom);
    setPanOffset({ x: 0, y: 0 });
  }, [minZoom, animationMs, triggerAnimation]);

  /**
   * Animate to a normalized 0–1 bbox: zoom so it fills ~30% of viewport and
   * pan so its center lands at viewport center.
   */
  const zoomToBox = useCallback((bbox: NormalizedBox) => {
    const centerX = bbox.left + bbox.width / 2;
    const centerY = bbox.top + bbox.height / 2;

    const size = Math.max(bbox.width, bbox.height);
    const targetZoom = size > 0
      ? Math.min(Math.max(0.3 / size, autoZoomLevel), maxZoom)
      : autoZoomLevel;

    const offsetX = (0.5 - centerX) * 100;
    const offsetY = (0.5 - centerY) * 100;

    triggerAnimation(350);
    setZoom(targetZoom);
    setPanOffset(clampPan(offsetX, offsetY, targetZoom));
  }, [autoZoomLevel, maxZoom, clampPan, triggerAnimation]);

  // --- Wheel zoom (non-passive listener) ---
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const direction = e.deltaY < 0 ? 1 : -1;
    triggerAnimation(wheelAnimationMs);
    setZoom(prev => {
      const next = direction > 0
        ? Math.min(prev * wheelStep, maxZoom)
        : Math.max(prev / wheelStep, minZoom);
      if (next <= minZoom) setPanOffset({ x: 0, y: 0 });
      return next;
    });
  }, [maxZoom, minZoom, wheelStep, wheelAnimationMs, triggerAnimation]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // --- Double-click zoom (toggles zoom-at-point / reset near max) ---
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const clickX = (e.clientX - rect.left) / rect.width;
    const clickY = (e.clientY - rect.top) / rect.height;

    triggerAnimation(animationMs);
    if (zoom >= maxZoom * 0.8) {
      setZoom(minZoom);
      setPanOffset({ x: 0, y: 0 });
      return;
    }
    // Convert screen click → element position accounting for current zoom/pan
    const elementX = (clickX - 0.5) / zoom + 0.5 - panOffset.x / 100;
    const elementY = (clickY - 0.5) / zoom + 0.5 - panOffset.y / 100;
    const targetZoom = Math.min(zoom * 2, maxZoom);
    const offsetX = (0.5 - elementX) * 100;
    const offsetY = (0.5 - elementY) * 100;
    setZoom(targetZoom);
    setPanOffset(clampPan(offsetX, offsetY, targetZoom));
  }, [zoom, panOffset, minZoom, maxZoom, animationMs, clampPan, triggerAnimation]);

  // --- Mouse drag panning ---
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoom <= minZoom) return;
    // Allow inner overlays (e.g. bounding boxes) to consume the drag.
    if ((e.target as HTMLElement).closest('[data-bbox]')) return;
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    dragStartPanRef.current = { x: panOffset.x, y: panOffset.y };
  }, [zoom, minZoom, panOffset]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    const el = containerRef.current;
    if (!el) return;
    e.preventDefault();
    const rect = el.getBoundingClientRect();
    const dx = (e.clientX - dragStartRef.current.x) * 100 / (rect.width * zoom);
    const dy = (e.clientY - dragStartRef.current.y) * 100 / (rect.height * zoom);
    setPanOffset(clampPan(
      dragStartPanRef.current.x + dx,
      dragStartPanRef.current.y + dy,
      zoom,
    ));
  }, [isDragging, zoom, clampPan]);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);
  const handleMouseLeave = useCallback(() => {
    if (isDragging) setIsDragging(false);
  }, [isDragging]);

  // --- Touch drag panning (single-touch only) ---
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (zoom <= minZoom || e.touches.length !== 1) return;
    const touch = e.touches[0];
    setIsDragging(true);
    dragStartRef.current = { x: touch.clientX, y: touch.clientY };
    dragStartPanRef.current = { x: panOffset.x, y: panOffset.y };
  }, [zoom, minZoom, panOffset]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || e.touches.length !== 1) return;
    const el = containerRef.current;
    if (!el) return;
    const touch = e.touches[0];
    const rect = el.getBoundingClientRect();
    const dx = (touch.clientX - dragStartRef.current.x) * 100 / (rect.width * zoom);
    const dy = (touch.clientY - dragStartRef.current.y) * 100 / (rect.height * zoom);
    setPanOffset(clampPan(
      dragStartPanRef.current.x + dx,
      dragStartPanRef.current.y + dy,
      zoom,
    ));
  }, [isDragging, zoom, clampPan]);

  const handleTouchEnd = useCallback(() => setIsDragging(false), []);

  /** DOM handlers ready to spread onto the viewport element. */
  const handlers = {
    onDoubleClick: handleDoubleClick,
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp,
    onMouseLeave: handleMouseLeave,
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  };

  return {
    /** Attach to the outer viewport element so the wheel listener can bind. */
    containerRef,
    /** Current zoom multiplier. */
    zoom,
    /** Current pan offset in percent (translate units). */
    panOffset,
    /** True while pointer-driven panning is active. */
    isDragging,
    /** True while a programmatic transition is mid-animation. */
    animating,
    /** Spread onto the viewport element. */
    handlers,
    /** Imperative API. */
    zoomIn,
    zoomOut,
    reset,
    zoomToBox,
    /** Escape hatches if a consumer needs lower-level control. */
    setZoom,
    setPanOffset,
    clampPan,
    /** Limits, exposed so consumers can reuse the same numbers in their UI. */
    minZoom,
    maxZoom,
  };
}
